/**
 * SalesAnalysisPage — Analyse des ventes Shopify par période et géographie.
 *
 * Groupe-compatible (pas de RequiresSingleEntity).
 * Mobile-first : 375px d'abord.
 */

import { useState, useMemo } from 'react';
import {
  ComposedChart,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { TrendingUp, ShoppingCart, ShoppingBag, Wallet, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import {
  useSalesSummary,
  useSalesByPeriod,
} from '../hooks/useSalesAnalytics.js';
import { formatCurrency, formatCurrencyShort } from '../lib/format.js';
import SalesLocationTable from '../components/SalesLocationTable.jsx';
import Sparkline from '../components/Sparkline.jsx';

// ── Date helpers ──────────────────────────────────────────────────────────────

function toISO(date) {
  return date.toISOString().slice(0, 10);
}

/**
 * Calcule les bornes API pour un raccourci de période.
 * `to` est exclusif côté SQL (< to), donc on passe le 1er du mois suivant, etc.
 */
function resolveShortcut(shortcut) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-indexed

  switch (shortcut) {
    case 'thisMonth': {
      const from = new Date(y, m, 1);
      const to = new Date(y, m + 1, 1);
      return { from: toISO(from), to: toISO(to) };
    }
    case 'lastMonth': {
      const from = new Date(y, m - 1, 1);
      const to = new Date(y, m, 1);
      return { from: toISO(from), to: toISO(to) };
    }
    case 'thisYear': {
      const from = new Date(y, 0, 1);
      const to = new Date(y + 1, 0, 1);
      return { from: toISO(from), to: toISO(to) };
    }
    case 'last12months': {
      const to = new Date(y, m + 1, 1);
      const from = new Date(y - 1, m + 1, 1);
      return { from: toISO(from), to: toISO(to) };
    }
    default:
      return null;
  }
}

/**
 * Traduit une date ISO "YYYY-MM-DD" sélectionnée par l'utilisateur (borne fin incluse)
 * en borne exclusive pour l'API : on ajoute 1 jour.
 */
function exclusiveTo(inclusiveTo) {
  if (!inclusiveTo) return '';
  const d = new Date(inclusiveTo + 'T00:00:00');
  d.setDate(d.getDate() + 1);
  return toISO(d);
}

/**
 * Granularité automatique selon la durée de la période.
 */
function autoGranularity(from, to) {
  if (!from || !to) return 'month';
  const days = Math.round((new Date(to) - new Date(from)) / 86400000);
  if (days <= 31) return 'day';
  if (days <= 84) return 'week';
  return 'month';
}

// ── Label XAxis selon granularité ─────────────────────────────────────────────

function formatXLabel(bucket, granularity) {
  if (!bucket) return '';
  const date = new Date(bucket + 'T00:00:00');
  if (granularity === 'month') {
    return date.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
  }
  if (granularity === 'week') {
    const jan4 = new Date(date.getFullYear(), 0, 4);
    const weekNum = Math.ceil(
      ((date - jan4) / 86400000 + jan4.getDay() + 1) / 7
    );
    return `S${String(weekNum).padStart(2, '0')}`;
  }
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

// ── Custom Tooltip Recharts ───────────────────────────────────────────────────

function SalesTooltip({ active, payload, label, granularity, mainData, compareData }) {
  if (!active || !payload?.length) return null;
  const items = payload.filter((p) => p.value != null);

  // Recherche les orders_count dans les séries brutes par index de label
  const mainBucket = Array.isArray(mainData)
    ? mainData.find((b) => formatXLabel(b.bucket, granularity) === label)
    : null;
  const compBucket = Array.isArray(compareData)
    ? compareData.find((b) => formatXLabel(b.bucket, granularity) === label)
    : null;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-slate-200 px-3 py-2.5 text-xs min-w-[190px]">
      <p className="font-semibold text-slate-700 mb-2 pb-1.5 border-b border-slate-100">{label}</p>
      {items.map((p) => {
        const isComp = p.dataKey === 'caComp';
        const bucket = isComp ? compBucket : mainBucket;
        return (
          <div key={p.dataKey} className="mt-1">
            <div className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-1.5">
                <span
                  className="inline-block w-2 h-2 rounded-full shrink-0"
                  style={{ background: p.color ?? p.fill ?? p.stroke }}
                />
                <span className="text-slate-600">{p.name}</span>
              </span>
              <span className="tabular-nums font-semibold text-slate-900">
                {formatCurrency(p.value * 100)}
              </span>
            </div>
            {bucket?.orders_count != null && (
              <p className="text-slate-400 mt-0.5 pl-3.5">
                {bucket.orders_count} commande{bucket.orders_count > 1 ? 's' : ''}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Skeleton helpers ───────────────────────────────────────────────────────────

function SkeletonBlock({ className = '' }) {
  return <div className={`bg-slate-100 animate-pulse rounded ${className}`} />;
}

// ── KPI Card ──────────────────────────────────────────────────────────────────

function SalesKpiCard({ label, value, delta, deltaPct, deltaAbs, icon: Icon, iconColor, loading, sparkData, sparkColor }) {
  const isPositive = delta !== null && delta !== undefined && delta > 0;
  const isNegative = delta !== null && delta !== undefined && delta < 0;
  const hasCompare = delta !== null && delta !== undefined;

  return (
    <div className="rounded-xl bg-white border border-slate-200 shadow-sm px-4 py-4 flex flex-col gap-2 relative overflow-hidden">
      {/* En-tête label + icône */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide leading-none">{label}</span>
        {Icon && (
          <span className={`p-1.5 rounded-lg ${iconColor ?? 'bg-slate-100'}`}>
            <Icon size={13} className="text-slate-600 shrink-0" aria-hidden />
          </span>
        )}
      </div>

      {/* Valeur principale */}
      {loading ? (
        <SkeletonBlock className="h-8 w-28 mt-1" />
      ) : (
        <p className="text-2xl font-bold tabular-nums text-slate-900 leading-tight">{value ?? '—'}</p>
      )}

      {/* Delta comparaison */}
      {!loading && hasCompare && (
        <div className={`flex items-center gap-1 text-xs font-medium tabular-nums ${
          isPositive ? 'text-green-600' : isNegative ? 'text-red-500' : 'text-slate-400'
        }`}>
          {isPositive && <ArrowUpRight size={12} aria-hidden />}
          {isNegative && <ArrowDownRight size={12} aria-hidden />}
          <span>
            {isPositive ? '+' : ''}{deltaAbs}
            {deltaPct !== null && deltaPct !== undefined && (
              <span className="opacity-70 ml-1">
                ({isPositive ? '+' : ''}{deltaPct.toFixed(1)} %)
              </span>
            )}
          </span>
        </div>
      )}

      {!loading && !hasCompare && <div className="h-4" />}

      {/* Sparkline en bas à droite */}
      {!loading && sparkData && sparkData.length >= 2 && (
        <div className="absolute bottom-2 right-2 opacity-60">
          <Sparkline data={sparkData} color={sparkColor ?? '#3b82f6'} width={72} height={24} />
        </div>
      )}
    </div>
  );
}

// ── Sélecteur de période — pills scrollables ──────────────────────────────────

const SHORTCUTS = [
  { key: 'thisMonth',    label: 'Ce mois' },
  { key: 'lastMonth',   label: 'Mois dernier' },
  { key: 'thisYear',    label: 'Cette année' },
  { key: 'last12months', label: '12 derniers mois' },
  { key: 'custom',      label: 'Personnalisé' },
];

/**
 * PeriodSelector — gère raccourcis pill + dates custom.
 * onChange({ from, to }) — `from` incluse, `to` exclusive (bornes API).
 * colorScheme : 'blue' | 'amber'
 */
function PeriodSelector({ value, onChange, colorScheme = 'blue', label }) {
  const [shortcut, setShortcut] = useState('thisMonth');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo,   setCustomTo]   = useState('');

  function handleShortcut(key) {
    setShortcut(key);
    if (key !== 'custom') {
      const resolved = resolveShortcut(key);
      if (resolved) onChange(resolved);
    }
  }

  function handleCustomChange(from, to) {
    if (from && to && from <= to) {
      onChange({ from, to: exclusiveTo(to) });
    }
  }

  const activePill =
    colorScheme === 'amber'
      ? 'bg-amber-600 text-white shadow-sm'
      : 'bg-blue-600 text-white shadow-sm';
  const inactivePill =
    colorScheme === 'amber'
      ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
      : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-transparent';
  const customBorder =
    colorScheme === 'amber' ? 'border-amber-300 focus:ring-amber-400' : 'border-slate-300 focus:ring-blue-400';

  return (
    <div className="space-y-2.5">
      {label && (
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest">{label}</p>
      )}
      {/* Pills scrollables horizontalement sur mobile */}
      <div className="flex gap-1.5 overflow-x-auto pb-0.5 -mx-1 px-1 scrollbar-none">
        {SHORTCUTS.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => handleShortcut(s.key)}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all whitespace-nowrap shrink-0 ${
              shortcut === s.key ? activePill : inactivePill
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {shortcut === 'custom' && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            value={customFrom}
            onChange={(e) => {
              setCustomFrom(e.target.value);
              handleCustomChange(e.target.value, customTo);
            }}
            className={`flex-1 min-w-[130px] text-sm border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 ${customBorder}`}
            aria-label="Date de début"
          />
          <span className="text-xs text-slate-400 shrink-0">→</span>
          <input
            type="date"
            value={customTo}
            min={customFrom}
            onChange={(e) => {
              setCustomTo(e.target.value);
              handleCustomChange(customFrom, e.target.value);
            }}
            className={`flex-1 min-w-[130px] text-sm border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 ${customBorder}`}
            aria-label="Date de fin"
          />
        </div>
      )}
    </div>
  );
}

// ── Graphique ventes ──────────────────────────────────────────────────────────

const GRANULARITY_LABEL = { day: 'Jour', week: 'Semaine', month: 'Mois' };

function SalesChart({ mainData, compareData, granularity, compareActive }) {
  const mainBuckets = Array.isArray(mainData)   ? mainData   : [];
  const compBuckets = Array.isArray(compareData) ? compareData : [];

  if (mainBuckets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 h-[200px]">
        <ShoppingBag size={28} className="text-slate-300" />
        <p className="text-sm text-slate-400">Aucune commande sur cette période.</p>
        <p className="text-xs text-slate-300">Essaie une période plus large.</p>
      </div>
    );
  }

  const chartData = mainBuckets.map((b, i) => ({
    label: formatXLabel(b.bucket, granularity),
    ca: b.ca_cents / 100,
    caComp: compBuckets[i] ? compBuckets[i].ca_cents / 100 : null,
  }));

  if (compareActive) {
    // Deux séries : Area courante + ligne comparaison
    return (
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
          <defs>
            <linearGradient id="areaMain" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.25} />
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="areaComp" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="#f59e0b" stopOpacity={0.15} />
              <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#94a3b8' }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tickFormatter={(v) => formatCurrencyShort(v * 100)}
            tick={{ fontSize: 11, fill: '#94a3b8' }}
            tickLine={false}
            axisLine={false}
            width={56}
            tickCount={4}
          />
          <Tooltip
            content={
              <SalesTooltip
                granularity={granularity}
                mainData={mainBuckets}
                compareData={compBuckets}
                compareActive
              />
            }
          />
          <Legend
            verticalAlign="top"
            align="right"
            wrapperStyle={{ fontSize: '11px', paddingBottom: '8px' }}
          />
          {/* eslint-disable-next-line react/no-unknown-property */}
          <Area
            type="monotone"
            dataKey="ca"
            name="Période courante"
            stroke="#3b82f6"
            strokeWidth={2}
            fill="url(#areaMain)"
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0 }}
          />
          <Area
            type="monotone"
            dataKey="caComp"
            name="Comparaison"
            stroke="#f59e0b"
            strokeWidth={1.5}
            strokeDasharray="4 3"
            fill="url(#areaComp)"
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    );
  }

  // Série unique : AreaChart simple
  return (
    <ResponsiveContainer width="100%" height={280}>
      <AreaChart data={chartData} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
        <defs>
          <linearGradient id="areaMainSingle" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.2} />
            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11, fill: '#94a3b8' }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tickFormatter={(v) => formatCurrencyShort(v * 100)}
          tick={{ fontSize: 11, fill: '#94a3b8' }}
          tickLine={false}
          axisLine={false}
          width={56}
          tickCount={4}
        />
        <Tooltip
          content={
            <SalesTooltip
              granularity={granularity}
              mainData={mainBuckets}
              compareData={[]}
              compareActive={false}
            />
          }
        />
        <Area
          type="monotone"
          dataKey="ca"
          name="CA TTC"
          stroke="#3b82f6"
          strokeWidth={2}
          fill="url(#areaMainSingle)"
          dot={false}
          activeDot={{ r: 4, strokeWidth: 0, fill: '#3b82f6' }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// ── Skeleton graphique ────────────────────────────────────────────────────────

function ChartSkeleton() {
  return (
    <div className="h-[280px] flex flex-col justify-end gap-2 px-2 pt-4 pb-2">
      <div className="flex items-end gap-1.5 h-full">
        {Array.from({ length: 10 }).map((_, i) => (
          <div
            key={i}
            className="flex-1 bg-slate-100 animate-pulse rounded-t"
            style={{ height: `${30 + Math.sin(i * 0.8) * 30 + 20}%` }}
          />
        ))}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function SalesAnalysisPage() {
  const [mainPeriod, setMainPeriod] = useState(() => resolveShortcut('thisMonth'));
  const [compareActive, setCompareActive] = useState(false);
  const [compPeriod, setCompPeriod] = useState(() => resolveShortcut('lastMonth'));

  const granularity = useMemo(
    () => autoGranularity(mainPeriod?.from, mainPeriod?.to),
    [mainPeriod]
  );

  // ── Data fetching ───────────────────────────────────────────────────────────

  const summaryMain = useSalesSummary({ from: mainPeriod?.from, to: mainPeriod?.to });
  const summaryComp = useSalesSummary({
    from: compareActive ? compPeriod?.from : undefined,
    to:   compareActive ? compPeriod?.to   : undefined,
  });

  const periodMain = useSalesByPeriod({
    from: mainPeriod?.from,
    to:   mainPeriod?.to,
    granularity,
  });
  const periodComp = useSalesByPeriod({
    from: compareActive ? compPeriod?.from : undefined,
    to:   compareActive ? compPeriod?.to   : undefined,
    granularity,
  });

  // ── KPI deltas ──────────────────────────────────────────────────────────────

  const mains = summaryMain.data;
  const comps = summaryComp.data;

  function delta(key) {
    if (!compareActive || !mains || !comps) return null;
    return (mains[key] ?? 0) - (comps[key] ?? 0);
  }

  function deltaPct(key) {
    if (!compareActive || !mains || !comps) return null;
    const base = comps[key] ?? 0;
    if (base === 0) return null;
    return ((mains[key] - base) / base) * 100;
  }

  // Valeur absolue du delta formatée pour affichage
  const dCaCents = delta('ca_cents');
  const dOrders  = delta('orders_count');
  const dBasket  = delta('average_basket_cents');

  // Sparkline identique pour les 3 cartes (série CA de la période principale)
  const sparkData = Array.isArray(periodMain.data) ? periodMain.data : [];

  // CA header — valeur courante pour l'executive summary
  const headerCa = mains ? formatCurrencyShort(mains.ca_cents) : null;
  const headerDelta = compareActive && dCaCents !== null ? dCaCents : null;

  return (
    <div className="space-y-6">

      {/* ── Header executive summary ──────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-700 rounded-2xl px-5 py-5 text-white shadow-md">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium text-white/60 uppercase tracking-widest mb-1">
              Analyse des ventes
            </p>
            <p className="text-xs text-white/50">CA Shopify · période sélectionnée</p>
          </div>
          <TrendingUp size={18} className="text-white/40 shrink-0 mt-0.5" aria-hidden />
        </div>

        {/* CA principal en grand */}
        <div className="mt-4 flex items-end gap-3 flex-wrap">
          {summaryMain.isLoading ? (
            <SkeletonBlock className="h-10 w-36 bg-white/10" />
          ) : (
            <span className="text-4xl font-bold tabular-nums leading-none">
              {headerCa ?? '—'}
            </span>
          )}

          {/* Delta CA vs comparaison */}
          {!summaryMain.isLoading && compareActive && headerDelta !== null && (
            <span
              className={`flex items-center gap-1 text-sm font-semibold tabular-nums mb-0.5 ${
                headerDelta >= 0 ? 'text-green-400' : 'text-red-400'
              }`}
            >
              {headerDelta >= 0
                ? <ArrowUpRight size={15} aria-hidden />
                : <ArrowDownRight size={15} aria-hidden />
              }
              {headerDelta >= 0 ? '+' : ''}{formatCurrencyShort(headerDelta)}
              {deltaPct('ca_cents') !== null && (
                <span className="opacity-70 text-xs">
                  ({headerDelta >= 0 ? '+' : ''}{deltaPct('ca_cents').toFixed(1)} %)
                </span>
              )}
            </span>
          )}
        </div>

        {compareActive && !summaryMain.isLoading && (
          <p className="text-xs text-white/40 mt-1">vs période de comparaison</p>
        )}
      </div>

      {/* ── Bloc périodes ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4 space-y-4">
        <PeriodSelector
          value={mainPeriod}
          onChange={setMainPeriod}
          colorScheme="blue"
          label="Période"
        />

        {/* Toggle comparaison */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={compareActive}
            onClick={() => setCompareActive((v) => !v)}
            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 ${
              compareActive ? 'bg-amber-500' : 'bg-slate-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                compareActive ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </button>
          <span className="text-sm text-slate-700 font-medium">
            Comparer avec une autre période
          </span>
        </div>

        {compareActive && (
          <div className="border-l-2 border-amber-300 pl-3">
            <PeriodSelector
              value={compPeriod}
              onChange={setCompPeriod}
              colorScheme="amber"
              label="Période de comparaison"
            />
          </div>
        )}
      </div>

      {/* ── KPI Cards ─────────────────────────────────────────────────────── */}
      {/* 1 colonne sur 375, 2 sur md, 3 sur lg */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <SalesKpiCard
          label="CA TTC"
          icon={Wallet}
          iconColor="bg-blue-50"
          loading={summaryMain.isLoading}
          value={mains ? formatCurrencyShort(mains.ca_cents) : '—'}
          delta={compareActive ? dCaCents : null}
          deltaAbs={dCaCents !== null ? formatCurrencyShort(Math.abs(dCaCents)) : null}
          deltaPct={compareActive ? deltaPct('ca_cents') : null}
          sparkData={sparkData}
          sparkColor="#3b82f6"
        />
        <SalesKpiCard
          label="Commandes"
          icon={ShoppingBag}
          iconColor="bg-violet-50"
          loading={summaryMain.isLoading}
          value={mains ? mains.orders_count?.toLocaleString('fr-FR') : '—'}
          delta={compareActive ? dOrders : null}
          deltaAbs={
            dOrders !== null
              ? `${Math.abs(dOrders)} cmd.`
              : null
          }
          deltaPct={compareActive ? deltaPct('orders_count') : null}
          sparkData={sparkData}
          sparkColor="#8b5cf6"
        />
        <SalesKpiCard
          label="Panier moyen"
          icon={ShoppingCart}
          iconColor="bg-emerald-50"
          loading={summaryMain.isLoading}
          value={mains ? formatCurrencyShort(mains.average_basket_cents) : '—'}
          delta={compareActive ? dBasket : null}
          deltaAbs={dBasket !== null ? formatCurrencyShort(Math.abs(dBasket)) : null}
          deltaPct={compareActive ? deltaPct('average_basket_cents') : null}
          sparkData={sparkData}
          sparkColor="#10b981"
        />
      </div>

      {/* Erreur résumé */}
      {summaryMain.error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          Impossible de charger les KPI : {summaryMain.error.message}
        </div>
      )}

      {/* ── Graphique ─────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-sm font-semibold text-slate-800">CA par période</h2>
          <span className="text-xs text-slate-400 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full">
            {GRANULARITY_LABEL[granularity] ?? granularity}
          </span>
        </div>

        {periodMain.isLoading ? (
          <ChartSkeleton />
        ) : (
          <SalesChart
            mainData={periodMain.data}
            compareData={compareActive ? periodComp.data : null}
            granularity={granularity}
            compareActive={compareActive}
          />
        )}

        {periodMain.error && (
          <p className="text-sm text-red-600 mt-2">
            Impossible de charger le graphique : {periodMain.error.message}
          </p>
        )}
      </div>

      {/* ── Tableau géographique ──────────────────────────────────────────── */}
      {mainPeriod?.from && mainPeriod?.to && (
        <SalesLocationTable from={mainPeriod.from} to={mainPeriod.to} />
      )}
    </div>
  );
}
